import React from 'react';
import type { DicomStudy, DicomInstanceInfo } from '../../types';

interface DicomMetadataPanelProps {
  study: DicomStudy;
  currentInstance?: DicomInstanceInfo;
}

export const DicomMetadataPanel: React.FC<DicomMetadataPanelProps> = ({ study, currentInstance }) => {
  return (
    <div className="w-72 bg-gray-900 text-gray-300 overflow-y-auto border-l border-gray-700 text-xs">
      {/* Patient Info */}
      <div className="p-3 border-b border-gray-700">
        <h4 className="text-gray-400 uppercase tracking-wider text-[10px] mb-2">Patient</h4>
        <MetadataRow label="Name" value={study.patientName} />
        <MetadataRow label="Study Date" value={study.studyDate} />
      </div>

      {/* Study Info */}
      <div className="p-3 border-b border-gray-700">
        <h4 className="text-gray-400 uppercase tracking-wider text-[10px] mb-2">Study</h4>
        <MetadataRow label="Modality" value={study.modality} />
        <MetadataRow label="Description" value={study.studyDescription} />
        <MetadataRow label="Body Part" value={study.bodyPartExamined} />
        <MetadataRow label="Institution" value={study.institutionName} />
        <MetadataRow label="Referring" value={study.referringPhysicianName} />
        <MetadataRow label="Images" value={String(study.instanceCount)} />
      </div>

      {/* Series Info */}
      {study.seriesDescription && (
        <div className="p-3 border-b border-gray-700">
          <h4 className="text-gray-400 uppercase tracking-wider text-[10px] mb-2">Series</h4>
          <MetadataRow label="Description" value={study.seriesDescription} />
        </div>
      )}

      {/* Current Instance Info */}
      {currentInstance && (
        <div className="p-3 border-b border-gray-700">
          <h4 className="text-gray-400 uppercase tracking-wider text-[10px] mb-2">Current Slice</h4>
          <MetadataRow label="Instance #" value={currentInstance.instanceNumber?.toString()} />
          <MetadataRow label="Slice Location" value={currentInstance.sliceLocation?.toFixed(2)} />
          <MetadataRow label="Matrix" value={
            currentInstance.rows && currentInstance.columns
              ? `${currentInstance.rows} x ${currentInstance.columns}`
              : undefined
          } />
          <MetadataRow label="WC / WW" value={
            currentInstance.windowCenter != null && currentInstance.windowWidth != null
              ? `${currentInstance.windowCenter} / ${currentInstance.windowWidth}`
              : undefined
          } />
        </div>
      )}

      {/* LLM Description */}
      {study.llmDescription && (
        <div className="p-3">
          <h4 className="text-gray-400 uppercase tracking-wider text-[10px] mb-2">AI Description</h4>
          <p className="text-gray-300 leading-relaxed">{study.llmDescription}</p>
        </div>
      )}
    </div>
  );
};

const MetadataRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-200 text-right max-w-[60%] truncate" title={value}>{value}</span>
    </div>
  );
};
